import { useLayoutEffect, useRef, useState } from "react";

/**
 * A GRADE QUE ABRE E FECHA ANIMADA — a altura medida, porque `auto` não interpola.
 *
 * CSS não transiciona de `0` para `auto`: o navegador não sabe o valor final antes do
 * layout, e a declaração simplesmente não anima (fica o corte seco). As saídas
 * conhecidas são três, e duas não servem aqui:
 *
 *   `max-height` fixo        pede um número que sirva a toda região. A grade tem 22
 *                           cartas em 2 colunas no celular e 6 no desktop — a altura
 *                           varia de ~600 a ~1900px. Um teto folgado faz a animação
 *                           "chegar" antes do fim (a altura real acaba e o resto do
 *                           tempo não mexe nada), e um teto justo corta a grade;
 *   `grid-template-rows`    de `0fr` para `1fr` anima de verdade, e é a solução limpa
 *                           — mas é Chrome 117+, e este módulo tem alvo conservador
 *                           por decisão (nem `color-mix`, nem `margin-inline`);
 *   medir e animar em px    o que este hook faz.
 *
 * POR QUE O `hidden` PRECISA DE UM ESTADO PRÓPRIO
 * -----------------------------------------------
 * A grade recolhida leva `hidden`, e o CSS a esconde com `display: none` — é o que
 * mantém as 22 cartas no DOM (o Ctrl+F acha o nome de uma carta guardada) sem baixar
 * imagem nenhuma, porque `loading="lazy"` não busca dentro de `display: none`.
 *
 * Só que `display: none` não anima, e um elemento nesse estado tem `scrollHeight` 0.
 * Se o `hidden` viesse direto de `aberta`, ao fechar o React o aplicaria no mesmo
 * render — a grade sumiria ANTES de o efeito rodar, e não haveria altura para medir
 * nem nada na tela para animar. Daí o estado `fechando`: enquanto ele vale, o
 * elemento continua no fluxo, e o `hidden` só chega quando a altura já é zero.
 *
 * A duração é PROPORCIONAL À ALTURA, com teto
 * -------------------------------------------
 * Uma duração fixa serve a uma altura só. Com 300ms, uma grade de 1900px desce a
 * 6300px/s — lê como um estouro, não como abrir; e uma de 600px na mesma duração
 * parece arrastada. Então a duração cresce com a distância e é limitada nas duas
 * pontas: sem piso, a região de 2 cartas pisca; sem teto, a de 22 no celular viraria
 * meio segundo de espera a cada toque.
 *
 * A CURVA VEM DO CSS (`--tl-ease`), lida do elemento em vez de copiada para cá: o
 * módulo tem uma curva só, e duplicar o `cubic-bezier` aqui criaria uma segunda
 * fonte da verdade que ninguém saberia atualizar junto.
 */

/* Piso, e quanto a duração cresce por 1000px de altura. Os dois medidos a olho na
   grade real: 240ms é onde a animação deixa de piscar, e o teto de 460 é onde ela
   começa a parecer que o painel está pensando. */
const DURACAO_MINIMA = 240;
const DURACAO_MAXIMA = 460;
const MS_POR_MIL_PIXELS = 140;

const EASE_PADRAO = "cubic-bezier(0.4, 0, 0.2, 1)";

/** A duração desta abertura, em ms, pela altura que a grade vai percorrer. */
export function duracaoDoRecolhimento(altura) {
  const bruta = DURACAO_MINIMA + (altura / 1000) * MS_POR_MIL_PIXELS;
  return Math.round(Math.min(DURACAO_MAXIMA, Math.max(DURACAO_MINIMA, bruta)));
}

function movimentoReduzido() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function curvaDoModulo(elemento) {
  const valor = getComputedStyle(elemento).getPropertyValue("--tl-ease").trim();
  return valor || EASE_PADRAO;
}

/* O que a animação escreve no elemento, e que o fim tem de limpar SEM SOBRAS.
   `overflow` importa mais que parece: a carta rara tem um selo `absolute` que sobra
   para fora da grade, e `overflow: hidden` esquecido ali o cortaria — um defeito que
   apareceria uma região depois, e só nas raras.

   A `height` NÃO É LIMPA AQUI, e é o conserto da piscada de 20/08/2026: ao fechar, o
   elemento fica com `height: 0px`. Ver `finalizar`. */
function limpar(elemento) {
  elemento.style.overflow = "";
  elemento.style.opacity = "";
  elemento.style.transition = "";
}

/* O ESTADO FINAL, e por que a altura fica em `0px` no fechamento.
   -------------------------------------------------------------
   A PISCADA: até 20/08/2026 o fim do fechamento limpava `height` junto com o resto, e
   só depois o `hidden` chegava — pelo React, no render do `setFechando(false)`. Entre
   as duas coisas a grade estava sem `hidden` e sem `height`, ou seja de volta à altura
   natural e opaca: 1246px de conteúdo reaparecendo por um quadro, com a página
   saltando. Medido em navegador real, quadro a quadro, em
   `ferramentas-diagnostico/diagnosticar-piscada-collapse.js`.

   A primeira correção foi pôr o `hidden` antes da limpeza. Funciona, mas apoia o
   conserto numa ORDEM — e ordem é frágil: o React mexe no atributo `hidden` três vezes
   num fechamento (aplica no render do clique, o efeito remove para medir, e ele volta
   no fim), então qualquer mudança no fluxo pode reintroduzir o defeito sem que nada
   grite.

   DEIXAR `height: 0px` tira a ordem da equação. Com altura zero, a grade não ocupa
   espaço nem que o `hidden` demore — não há estado intermediário visível, em nenhuma
   ordem. E não sobra sujeira para a próxima abertura, porque o efeito limpa a altura
   antes de medir. */
function finalizar(elemento, aberta) {
  limpar(elemento);
  if (aberta) {
    elemento.style.height = "";
  } else {
    elemento.hidden = true;
    elemento.style.height = "0px";
  }
}

/**
 * SERVE A DOIS TIPOS DE RECOLHIMENTO, e `oculto` é o que os une:
 *
 *   a GRADE de uma região (`nucleo/ColecaoDeCartas`) o usa no atributo `hidden` — as
 *   cartas ficam no DOM, e é `display: none` que as esconde;
 *   as SEÇÕES da página de conquistas o usam para MONTAR ou não o conteúdo, porque ali
 *   a decisão é desmontar (a coleção tem até 110 imagens, e a seção recolhida não deve
 *   pagar por elas — ver `SecaoDeConquista`).
 *
 * Nos dois casos o problema é o mesmo: o estado final some do fluxo, e o que sumiu não
 * anima. `oculto` é o adiamento — ele só vira `true` quando a altura já chegou a zero.
 *
 * @param {object}  ref     ref do elemento que recolhe
 * @param {boolean} aberta
 * @returns {{ oculto: boolean }} `hidden` do elemento, ou "não monte o conteúdo"
 */
export function useRecolhimentoAnimado(ref, aberta) {
  const [fechando, setFechando] = useState(false);

  /* `fechando` DERIVADO NO RENDER, e não num efeito, e isto é o que faz o hook servir
     ao desmonte além do `hidden`.

     Num efeito, `fechando` só viraria `true` DEPOIS do render em que `aberta` virou
     `false` — e nesse render `oculto` já seria `true`. Para o `hidden` dava para
     consertar à mão (o efeito removia o atributo antes de medir), mas para o desmonte
     não dá: o conteúdo já saiu da árvore, e o React não o remonta por baixo do pano. A
     altura a medir seria zero.

     É o padrão que o próprio React documenta para ajustar estado quando uma prop muda:
     comparar com o valor anterior guardado em estado e chamar o setter durante o
     render. O React reexecuta o componente ANTES de comitar, então quando o efeito
     roda o conteúdo ainda está montado. */
  const [aberturaAnterior, setAberturaAnterior] = useState(aberta);
  if (aberturaAnterior !== aberta) {
    setAberturaAnterior(aberta);
    setFechando(!aberta);
  }

  /* A MONTAGEM NÃO ANIMA. A coleção nasce com uma região aberta e as outras fechadas
     (ver `regiaoAbertaPorPadrao`), e animar isso faria a página abrir com painéis se
     desdobrando sozinhos — movimento que não responde a gesto nenhum do aluno. */
  const jaMontou = useRef(false);

  useLayoutEffect(() => {
    const elemento = ref.current;
    if (!elemento) return undefined;

    if (!jaMontou.current) {
      jaMontou.current = true;
      return undefined;
    }

    if (movimentoReduzido()) {
      /* Sob esta preferência o comportamento é o de antes deste hook: corte seco. Não
         é degradação — é o que a preferência pede. */
      finalizar(elemento, aberta);
      setFechando(false);
      return undefined;
    }

    /* `hidden` REMOVIDO À MÃO, e não pelo React. Ao fechar, este efeito roda no mesmo
       ciclo em que `aberta` virou `false`, e o atributo já está no DOM; medir agora
       daria zero. O React reconcilia no render seguinte (o do `setFechando`), e chega
       ao mesmo valor — então não há briga, só ordem. */
    elemento.hidden = false;

    /* A ALTURA LIMPA ANTES DE MEDIR. A grade fechada fica com `height: 0px` (ver
       `finalizar`), e medir `scrollHeight` com ela ali é pedir para o navegador
       responder sobre uma caixa de altura zero. Limpar devolve a altura natural, que é
       o alvo da abertura. */
    elemento.style.height = "";

    const alvo = elemento.scrollHeight;
    const duracao = duracaoDoRecolhimento(alvo);
    const curva = curvaDoModulo(elemento);

    elemento.style.overflow = "hidden";
    elemento.style.height = `${aberta ? 0 : alvo}px`;
    elemento.style.opacity = aberta ? "0" : "1";

    /* Ler `offsetHeight` FORÇA o layout, e é o que separa o estado inicial do final em
       dois quadros. Sem isso o navegador junta os dois na mesma recalculação e não há
       transição nenhuma — o valor pula direto para o fim. */
    // eslint-disable-next-line no-unused-expressions
    elemento.offsetHeight;

    elemento.style.transition = `height ${duracao}ms ${curva}, opacity ${duracao}ms ${curva}`;
    elemento.style.height = `${aberta ? alvo : 0}px`;
    elemento.style.opacity = aberta ? "1" : "0";

    if (!aberta) setFechando(true);

    /* Declarado antes de `aoTerminar` para a closure alcançá-lo, e atribuído depois:
       um `let` e não um ref, porque o que precisa cancelar isto é o CLEANUP deste
       mesmo efeito — e o cleanup vive na mesma closure.

       Não há um ref guardando "a animação em curso", e não é esquecimento: quando o
       aluno toca de novo, `aberta` muda, e o React roda o cleanup do efeito anterior
       ANTES do novo. O timer velho morre ali. Um ref para o mesmo fim seria um segundo
       caminho de cancelamento que nenhum teste conseguiria exercitar. */
    let timer;

    function terminar() {
      finalizar(elemento, aberta);
      /* `setFechando(false)` é o que faz o React concordar com o DOM. O atributo já
         está lá — então ele não muda nada na tela; o que muda é o modelo do React
         voltar a bater com o elemento. */
      if (!aberta) setFechando(false);
    }

    function aoTerminar(evento) {
      /* Só a transição de ALTURA deste elemento: `opacity` termina junto e chamaria o
         fim duas vezes, e um `transitionend` de carta borbulharia até aqui. */
      if (evento.target !== elemento || evento.propertyName !== "height")
        return;
      clearTimeout(timer);
      elemento.removeEventListener("transitionend", aoTerminar);
      terminar();
    }

    elemento.addEventListener("transitionend", aoTerminar);

    /* O TIMER É A REDE, e não o caminho normal. `transitionend` não dispara quando a
       aba está em segundo plano, quando a transição é interrompida por outra, e nunca
       no jsdom — onde `scrollHeight` é 0 e não há motor de animação. Sem ele, a grade
       fechada ficaria com `height: 0` e SEM `hidden`: invisível na tela e ainda
       alcançável pelo leitor de tela, que é o pior dos dois mundos. */
    timer = setTimeout(() => {
      elemento.removeEventListener("transitionend", aoTerminar);
      terminar();
    }, duracao + 60);

    /* O CLEANUP É QUEM CANCELA a animação em curso, e é por isso que ele não é só
       higiene: sem o `clearTimeout` daqui, um toque de reabrir deixaria o timer do
       fechamento vivo — ele dispararia no meio da abertura e chamaria a limpeza, que
       zera `height` e `transition`. O sintoma é a grade pular para a altura final em
       vez de crescer. */
    return () => {
      clearTimeout(timer);
      elemento.removeEventListener("transitionend", aoTerminar);
    };
  }, [aberta, ref]);

  return { oculto: !aberta && !fechando };
}

export default useRecolhimentoAnimado;
